import { CommonModule } from "@angular/common";
import { ChangeDetectionStrategy, Component, computed, linkedSignal, signal } from "@angular/core";
import { FormsModule } from "@angular/forms";
import { TypeManagerDecorator } from "../../../main/type.map.service";
import { s25Resource } from "../../../util/s25.resource";
import { S25Util } from "../../../util/s25-util";
import { AlertService } from "../../../services/alert.service";
import { StateService } from "../../../services/state.service";
import { S25ButtonComponent } from "../../../standalone/s25.button.component";
import { S25DndSortableModule } from "../../s25-dnd-sortable/s25.dnd.sortable.module";
import { S25IconComponent } from "../../s25-icon/s25.icon.component";
import { TrailApi, type CohortDto, type CopyTarget, type CurricCopyMode } from "./trail.api";
import { TrailCohortAdminComponent } from "./trail.cohort.admin.component";
import { TrailCurriculumCreateComponent } from "./trail.curriculum.create.component";
import { TrailCurriculumEditorComponent } from "./trail.curriculum.editor.component";
import { TrailFocusAreaAdminComponent } from "./trail.focus.area.admin.component";
import { TrailRequirementLibraryComponent } from "./trail.requirement.library.component";
import { TrailLabels } from "./trail.labels";
import {
    changedSortOrders,
    createSortOrderSaveQueue,
    SORT_ORDER_PERSIST_DEBOUNCE_MS,
} from "../../../util/sort.order.util";

interface CurriculumListItem {
    defnId: number;
    name: string;
    displayName?: string;
    description?: string;
    catalogDescription?: string;
    status: "DRAFT" | "PUBLISHED" | "DISABLED";
    cohortCode?: string;
    cohortId?: number;
    endingAwardKey?: string;
    endingAwardDisplayName?: string;
    catalogSortOrder?: number;
}

@TypeManagerDecorator("s25-ng-trail-admin-page")
@Component({
    selector: "s25-ng-trail-admin-page",
    standalone: true,
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [
        CommonModule,
        FormsModule,
        S25ButtonComponent,
        S25DndSortableModule,
        S25IconComponent,
        TrailCohortAdminComponent,
        TrailCurriculumCreateComponent,
        TrailCurriculumEditorComponent,
        TrailFocusAreaAdminComponent,
        TrailRequirementLibraryComponent,
    ],
    host: { class: "trail-admin-page" },
    template: `
        <div class="c-padding--double">
            @if (activeView() === "cohorts") {
                <s25-ng-trail-cohort-admin (backToAdmin)="backToList()" />
            } @else if (activeView() === "focusAreas") {
                <s25-ng-trail-focus-area-admin (backToAdmin)="backToList()" />
            } @else if (activeView() === "requirementLibrary") {
                <s25-ng-trail-requirement-library [cohortId]="selectedCohortFilter()" (backToAdmin)="backToList()" />
            } @else if (activeView() === "create") {
                <s25-ng-trail-curriculum-create (cancel)="backToList()" (created)="backToList()" />
            } @else if (activeView() === "edit") {
                @if (editingDefnId(); as defnId) {
                    <s25-ng-trail-curriculum-editor [defnId]="defnId" (backToList)="backToList()" />
                }
            } @else {
                <h1>{{ labels.TRAILS }} Admin</h1>
                <div class="trail-admin-page__toolbar c-margin-bottom--single">
                    <s25-ng-button [type]="'outline'" [onClick]="navigateToCohorts">
                        Manage {{ labels.COHORTS }}
                    </s25-ng-button>
                    <s25-ng-button [type]="'outline'" [onClick]="navigateToFocusAreas">
                        Manage {{ labels.FOCUS_AREAS }}
                    </s25-ng-button>
                    <s25-ng-button [type]="'outline'" [onClick]="navigateToExperiences">
                        Review {{ labels.EXPERIENCES }}
                    </s25-ng-button>
                    <s25-ng-button [type]="'outline'" [onClick]="navigateToRequirementLibrary">
                        {{ labels.REQUIREMENT }} Library
                    </s25-ng-button>
                </div>
                <div class="trail-admin-page__list-heading">
                    <h2>{{ labels.CURRICULA }}</h2>
                    <s25-ng-button data-id="new-curriculum" [type]="'primary'" [onClick]="navigateToCreate">
                        New {{ labels.CURRICULUM }}
                    </s25-ng-button>
                </div>
                <div class="trail-admin-page__filter c-margin-bottom--single">
                    <label>{{ labels.COHORT }} selection (and filter):</label>
                    <select
                        class="c-input"
                        [ngModel]="selectedCohortFilter()"
                        (ngModelChange)="selectedCohortFilter.set($event)"
                    >
                        <option [ngValue]="null">All {{ labels.COHORTS }}</option>
                        @for (cohort of cohorts.value() ?? []; track cohort.cohortId) {
                            <option [ngValue]="cohort.cohortId">{{ cohort.cohortCode }}</option>
                        }
                    </select>
                    <s25-ng-button data-id="refresh-list" [type]="'outline'" [onClick]="refreshList">
                        Refresh
                    </s25-ng-button>
                </div>
                @if (curricula.isLoading()) {
                    <p>Loading…</p>
                } @else if (curricula.error()) {
                    <p class="c-text--error">{{ formatError(curricula.error()) }}</p>
                } @else {
                    @if (!filteredCurricula().length) {
                        <p class="c-text--secondary">No curricula found.</p>
                    } @else {
                        <table class="c-table trail-admin-page__table">
                            <thead>
                                <tr>
                                    <th class="trail-admin-page__drag-col"></th>
                                    <th>Name</th>
                                    <th>{{ labels.COHORT }}</th>
                                    <th>Status</th>
                                    <th>{{ labels.ENDING_AWARD }}</th>
                                    <th>Actions</th>
                                </tr>
                            </thead>
                            <tbody
                                s25-ng-dnd-sortable
                                [items]="sortableCurricula()"
                                (orderChanged)="onCurriculumOrderChanged($event)"
                            >
                                @for (
                                    curriculum of sortableCurricula();
                                    track curriculum.defnId;
                                    let curriculumIndex = $index
                                ) {
                                    <tr s25-ng-dnd-sortable-item [index]="curriculumIndex" [draggable]="true">
                                        <td class="trail-admin-page__drag-col">
                                            <s25-ng-icon [type]="'dragHandle'" [ariaLabel]="'Reorder curriculum'" />
                                        </td>
                                        <td>{{ curriculum.displayName || curriculum.name }}</td>
                                        <td>{{ curriculum.cohortCode ?? "—" }}</td>
                                        <td>
                                            <span
                                                class="trail-admin-page__status-badge"
                                                [class.trail-admin-page__status-badge--draft]="
                                                    curriculum.status === 'DRAFT'
                                                "
                                                [class.trail-admin-page__status-badge--published]="
                                                    curriculum.status === 'PUBLISHED'
                                                "
                                                [class.trail-admin-page__status-badge--disabled]="
                                                    curriculum.status === 'DISABLED'
                                                "
                                            >
                                                {{ statusLabel(curriculum.status) }}
                                            </span>
                                        </td>
                                        <td>{{ curriculum.endingAwardKey ?? "—" }}</td>
                                        <td>
                                            <div class="trail-admin-page__actions">
                                                <s25-ng-button [type]="'outline'" [onClick]="editFactory(curriculum)">
                                                    Edit
                                                </s25-ng-button>
                                                <s25-ng-button [type]="'outline'" [onClick]="copyFactory(curriculum)">
                                                    Copy
                                                </s25-ng-button>
                                                @if (curriculum.status === "DRAFT") {
                                                    <s25-ng-button
                                                        [type]="'outline'"
                                                        [buttonClass]="'aw-button--primary'"
                                                        [onClick]="publishFactory(curriculum)"
                                                    >
                                                        Publish
                                                    </s25-ng-button>
                                                }
                                                @if (curriculum.status !== "DISABLED") {
                                                    <s25-ng-button
                                                        data-id="disable-curriculum"
                                                        [type]="'danger--outline'"
                                                        [onClick]="disableFactory(curriculum)"
                                                    >
                                                        Disable
                                                    </s25-ng-button>
                                                }
                                            </div>
                                        </td>
                                    </tr>
                                }
                            </tbody>
                        </table>
                    }
                }
            }

            @if (showCopyDialog()) {
                <div class="trail-admin-page__copy-overlay" (click)="closeCopyDialog()">
                    <div class="trail-admin-page__copy-dialog" (click)="$event.stopPropagation()">
                        <h3>Copy "{{ copySourceName() }}"</h3>
                        <p class="c-text--secondary">Select target {{ labels.COHORTS.toLowerCase() }}:</p>
                        <div class="trail-admin-page__copy-cohorts c-margin-bottom--single">
                            @for (cohort of cohorts.value() ?? []; track cohort.cohortId) {
                                <label class="trail-admin-page__copy-cohort-option">
                                    <input
                                        type="checkbox"
                                        [checked]="copySelectedCohortIds().includes(cohort.cohortId!)"
                                        (change)="toggleCopyCohort(cohort.cohortId!)"
                                    />
                                    {{ cohort.cohortCode }} ({{ cohort.cohortTypeCode }})
                                    @if (cohort.cohortId === copySourceCohortId()) {
                                        <span class="c-text--secondary">(same cohort)</span>
                                    }
                                </label>
                            }
                        </div>
                        @if (copyNeedsSameCohortOverrides()) {
                            <div class="trail-admin-page__copy-overrides c-margin-bottom--single">
                                <p class="c-text--secondary">
                                    Copying to the same cohort requires a new name and ending award key.
                                </p>
                                <div class="form-group c-margin-bottom--half">
                                    <label>New name *</label>
                                    <input
                                        class="c-input"
                                        [ngModel]="copySameCohortName()"
                                        (ngModelChange)="copySameCohortName.set($event)"
                                        placeholder="New curriculum name"
                                    />
                                </div>
                                <div class="form-group c-margin-bottom--half">
                                    <label>New {{ labels.ENDING_AWARD }} key *</label>
                                    <input
                                        class="c-input"
                                        [ngModel]="copySameCohortAwardKey()"
                                        (ngModelChange)="copySameCohortAwardKey.set($event)"
                                        placeholder="New award key"
                                    />
                                </div>
                            </div>
                        }
                        <fieldset class="trail-admin-page__copy-mode c-margin-bottom--single">
                            <legend>Requirement trees *</legend>
                            @if (copyReuseAvailable()) {
                                <label class="trail-admin-page__copy-mode-option">
                                    <input
                                        type="radio"
                                        name="copyMode"
                                        [checked]="copyMode() === 'REUSE'"
                                        (change)="copyMode.set('REUSE')"
                                    />
                                    Reuse matching requirements (by code)
                                </label>
                            }
                            <label class="trail-admin-page__copy-mode-option">
                                <input
                                    type="radio"
                                    name="copyMode"
                                    [checked]="copyMode() === 'FULL'"
                                    (change)="copyMode.set('FULL')"
                                />
                                Copy all requirements
                            </label>
                            <label class="trail-admin-page__copy-mode-option">
                                <input
                                    type="radio"
                                    name="copyMode"
                                    [checked]="copyMode() === 'SHELL'"
                                    (change)="copyMode.set('SHELL')"
                                />
                                Copy without requirements
                            </label>
                        </fieldset>
                        <div class="trail-admin-page__copy-actions">
                            <s25-ng-button
                                [type]="'primary'"
                                [onClick]="executeCopy"
                                [disabled]="!canCopy() || copyBusy()"
                            >
                                {{ copyBusy() ? "Copying…" : "Copy" }}
                            </s25-ng-button>
                            <s25-ng-button [type]="'danger--outline'" [onClick]="closeCopyDialog">Cancel</s25-ng-button>
                        </div>
                    </div>
                </div>
            }
        </div>
    `,
    styles: `
        .trail-admin-page__toolbar {
            display: flex;
            flex-wrap: wrap;
            align-items: center;
            gap: 0.75rem;
        }
        .trail-admin-page__list-heading {
            display: flex;
            align-items: center;
            gap: 0.75rem;
            flex-wrap: wrap;
            margin-bottom: 0.75rem;
        }
        .trail-admin-page__list-heading h2 {
            margin: 0;
        }
        .trail-admin-page__filter {
            display: flex;
            align-items: center;
            gap: 0.75rem;
        }
        .trail-admin-page__filter select {
            min-width: 12rem;
        }
        .trail-admin-page__table {
            width: 100%;
            border-collapse: collapse;
        }
        .trail-admin-page__table th,
        .trail-admin-page__table td {
            border: 1px solid var(--color--border, #ddd);
            padding: 0.5rem 0.75rem;
            text-align: left;
        }
        .trail-admin-page__status-badge {
            display: inline-block;
            padding: 0.15rem 0.5rem;
            border-radius: var(--border-radius);
            font-size: 0.85em;
            font-weight: 600;
        }
        .trail-admin-page__status-badge--draft {
            background: color-mix(in srgb, var(--color--warning) 18%, var(--color--surface-bg));
            color: var(--color--warning);
        }
        .trail-admin-page__status-badge--published {
            background: color-mix(in srgb, var(--color--text-success) 18%, var(--color--surface-bg));
            color: var(--color--text-success);
        }
        .trail-admin-page__status-badge--disabled {
            background: color-mix(in srgb, var(--color--danger) 18%, var(--color--surface-bg));
            color: var(--color--danger);
        }
        .trail-admin-page__actions {
            display: flex;
            flex-wrap: wrap;
            gap: 0.5rem;
        }
        .trail-admin-page__copy-overlay {
            position: fixed;
            inset: 0;
            z-index: 10000;
            display: flex;
            align-items: center;
            justify-content: center;
            background: rgba(0, 0, 0, 0.4);
        }
        .trail-admin-page__copy-dialog {
            background: var(--color--surface-bg);
            color: var(--color--text);
            border-radius: 8px;
            padding: 1.5rem;
            max-width: 500px;
            width: 90%;
            box-shadow: var(--shadow--dropdown);
        }
        .trail-admin-page__copy-dialog h3 {
            margin: 0 0 0.5rem;
        }
        .trail-admin-page__copy-cohorts {
            display: flex;
            flex-direction: column;
            gap: 0.4rem;
            max-height: 200px;
            overflow-y: auto;
        }
        .trail-admin-page__copy-cohort-option {
            display: flex;
            align-items: center;
            gap: 0.5rem;
            cursor: pointer;
        }
        .trail-admin-page__copy-overrides .form-group {
            display: grid;
            grid-template-columns: 10rem minmax(0, 1fr);
            gap: 0.5rem;
            align-items: center;
        }
        .trail-admin-page__copy-mode {
            border: 0;
            margin: 0;
            padding: 0;
            display: flex;
            flex-direction: column;
            gap: 0.4rem;
        }
        .trail-admin-page__copy-mode legend {
            font-weight: 600;
            margin-bottom: 0.25rem;
            padding: 0;
        }
        .trail-admin-page__copy-mode-option {
            display: flex;
            align-items: center;
            gap: 0.5rem;
            cursor: pointer;
        }
        .trail-admin-page__copy-actions {
            display: flex;
            gap: 0.75rem;
        }
        .trail-admin-page__drag-col {
            width: 1.75rem;
            text-align: center;
        }
        .trail-admin-page__table tbody.sortable [s25-ng-dnd-sortable-item] {
            cursor: grab;
        }
    `,
})
export class TrailAdminPageComponent {
    readonly labels = TrailLabels;
    private readonly listGen = signal(0);
    readonly selectedCohortFilter = signal<number | null>(null);
    readonly activeView = signal<"list" | "cohorts" | "focusAreas" | "create" | "edit" | "requirementLibrary">("list");
    readonly editingDefnId = signal<number | null>(null);

    readonly showCopyDialog = signal(false);
    readonly copySourceDefnId = signal<number | null>(null);
    readonly copySourceCohortId = signal<number | null>(null);
    readonly copySourceName = signal("");
    readonly copySelectedCohortIds = signal<number[]>([]);
    readonly copySameCohortName = signal("");
    readonly copySameCohortAwardKey = signal("");
    readonly copyMode = signal<CurricCopyMode>("FULL");
    readonly copyBusy = signal(false);

    readonly cohorts = s25Resource({
        params: () => this.listGen(),
        loader: () => TrailApi.getCohorts(),
    });

    readonly curricula = s25Resource({
        params: () => this.listGen(),
        loader: async () => {
            const [allCohorts, defns] = await Promise.all([TrailApi.getCohorts(), TrailApi.listCurricula()]);
            const cohortById = new Map((allCohorts ?? []).map((cohort) => [cohort.cohortId, cohort]));
            return (defns ?? []).map((defn) => {
                const published = defn.isPublished === true;
                const disabled = defn.isDisabled === true;
                const status: CurriculumListItem["status"] = disabled ? "DISABLED" : published ? "PUBLISHED" : "DRAFT";
                return {
                    defnId: defn.defnId,
                    name: defn.name ?? "",
                    displayName: defn.displayName,
                    description: defn.description,
                    catalogDescription: defn.catalogDescription,
                    status,
                    cohortCode: cohortById.get(defn.cohortId)?.cohortCode,
                    cohortId: defn.cohortId,
                    endingAwardKey: defn.endingAwardKey,
                    endingAwardDisplayName: defn.endingAwardDisplayName,
                    catalogSortOrder: defn.catalogSortOrder,
                };
            });
        },
    });

    readonly filteredCurricula = computed((): CurriculumListItem[] => {
        const allItems = this.curricula.value() ?? [];
        const cohortFilter = this.selectedCohortFilter();
        const filtered = cohortFilter == null ? allItems : allItems.filter((item) => item.cohortId === cohortFilter);
        return [...filtered].sort((itemA, itemB) => {
            const orderA = itemA.catalogSortOrder ?? Number.MAX_SAFE_INTEGER;
            const orderB = itemB.catalogSortOrder ?? Number.MAX_SAFE_INTEGER;
            if (orderA !== orderB) return orderA - orderB;
            return (itemA.displayName || itemA.name || "").localeCompare(
                itemB.displayName || itemB.name || "",
                undefined,
                { sensitivity: "base" },
            );
        });
    });

    readonly sortableCurricula = linkedSignal((): CurriculumListItem[] => this.filteredCurricula());
    private curriculumOrderPersistHandle: ReturnType<typeof setTimeout> | null = null;
    private readonly enqueueCurriculumOrderSave = createSortOrderSaveQueue();

    statusLabel(status: string): string {
        switch (status) {
            case "DRAFT":
                return TrailLabels.DRAFT;
            case "PUBLISHED":
                return TrailLabels.PUBLISHED;
            case "DISABLED":
                return TrailLabels.DISABLED;
            default:
                return status;
        }
    }

    formatError(error: unknown): string {
        if (error instanceof Error) return error.message;
        return String(error ?? "Unknown error");
    }

    onCurriculumOrderChanged(curricula: CurriculumListItem[]): void {
        this.sortableCurricula.set(curricula);
        this.queueCurriculumOrderPersist();
    }

    private queueCurriculumOrderPersist(): void {
        if (this.curriculumOrderPersistHandle != null) {
            clearTimeout(this.curriculumOrderPersistHandle);
        }
        this.curriculumOrderPersistHandle = setTimeout(() => {
            this.curriculumOrderPersistHandle = null;
            void this.enqueueCurriculumOrderSave(() => this.persistCurriculumOrder());
        }, SORT_ORDER_PERSIST_DEBOUNCE_MS);
    }

    private async persistCurriculumOrder(): Promise<void> {
        const curricula = this.sortableCurricula();
        const { orders, changes } = changedSortOrders(
            curricula,
            (curriculum) => curriculum.defnId,
            (curriculum) => curriculum.catalogSortOrder,
        );
        if (!changes.length) {
            return;
        }
        const updated = curricula.map((curriculum, curriculumIndex) => ({
            ...curriculum,
            catalogSortOrder: orders[curriculumIndex],
        }));
        this.sortableCurricula.set(updated);
        try {
            await TrailApi.updateCurriculumSortOrders(
                changes.map((change) => ({ defnId: change.id, catalogSortOrder: change.sortOrder })),
            );
        } catch (caughtError) {
            if (this.sortableCurricula() === updated) {
                this.sortableCurricula.set(curricula);
            }
            await AlertService.alert(S25Util.errorText(caughtError));
        }
    }

    refreshList = () => {
        this.listGen.update((count) => count + 1);
    };

    navigateToCreate = () => {
        this.activeView.set("create");
    };

    navigateToCohorts = () => {
        this.activeView.set("cohorts");
    };

    navigateToFocusAreas = () => {
        this.activeView.set("focusAreas");
    };

    navigateToExperiences = () => {
        void StateService.go("seTrailExperiences", null, null);
    };

    navigateToRequirementLibrary = () => {
        this.activeView.set("requirementLibrary");
    };

    backToList = () => {
        this.activeView.set("list");
        this.editingDefnId.set(null);
        this.listGen.update((count) => count + 1);
    };

    editFactory = (curriculum: CurriculumListItem) => {
        return () => {
            this.editingDefnId.set(curriculum.defnId);
            this.activeView.set("edit");
        };
    };

    readonly copyNeedsSameCohortOverrides = computed(() => {
        return this.copySourceCohortId() != null && this.copySelectedCohortIds().includes(this.copySourceCohortId()!);
    });

    readonly copyReuseAvailable = computed(() => {
        const sourceCohortId = this.copySourceCohortId();
        return this.copySelectedCohortIds().some((cohortId) => cohortId !== sourceCohortId);
    });

    readonly canCopy = computed(() => {
        if (!this.copySelectedCohortIds().length) return false;
        if (this.copyMode() === "REUSE" && !this.copyReuseAvailable()) return false;
        if (this.copyNeedsSameCohortOverrides()) {
            return !!this.copySameCohortName().trim() && !!this.copySameCohortAwardKey().trim();
        }
        return true;
    });

    copyFactory = (curriculum: CurriculumListItem) => {
        return () => {
            this.copySourceDefnId.set(curriculum.defnId);
            this.copySourceCohortId.set(curriculum.cohortId ?? null);
            this.copySourceName.set(curriculum.displayName || curriculum.name);
            this.copySelectedCohortIds.set([]);
            this.copySameCohortName.set("");
            this.copySameCohortAwardKey.set("");
            this.copyMode.set("FULL");
            this.showCopyDialog.set(true);
        };
    };

    toggleCopyCohort(cohortId: number): void {
        const current = this.copySelectedCohortIds();
        if (current.includes(cohortId)) {
            this.copySelectedCohortIds.set(current.filter((existingId) => existingId !== cohortId));
        } else {
            this.copySelectedCohortIds.set([...current, cohortId]);
        }
        if (this.copyMode() === "REUSE" && !this.copyReuseAvailable()) {
            this.copyMode.set("FULL");
        }
    }

    closeCopyDialog = () => {
        this.showCopyDialog.set(false);
    };

    executeCopy = async () => {
        if (!this.canCopy() || this.copyBusy()) return;
        this.copyBusy.set(true);
        try {
            const sourceCohortId = this.copySourceCohortId();
            const targets: CopyTarget[] = this.copySelectedCohortIds().map((cohortId) => {
                const isSameCohort = cohortId === sourceCohortId;
                return {
                    cohortId,
                    nameOverride: isSameCohort ? this.copySameCohortName().trim() : undefined,
                    endingAwardKeyOverride: isSameCohort ? this.copySameCohortAwardKey().trim() : undefined,
                };
            });
            const result = await TrailApi.copyCurriculum(this.copySourceDefnId()!, targets, this.copyMode());
            this.showCopyDialog.set(false);
            this.listGen.update((count) => count + 1);
            const skipped = result.skipped ?? [];
            if (skipped.length) {
                const reasons = skipped.map((skip) => skip.reason).join("\n");
                await AlertService.alert(`Copied with ${skipped.length} skip(s):\n${reasons}`);
            }
        } catch (caughtError) {
            await AlertService.alert(S25Util.errorText(caughtError));
        } finally {
            this.copyBusy.set(false);
        }
    };

    publishFactory = (curriculum: CurriculumListItem) => {
        return async () => {
            if (!(await AlertService.confirm(`Publish "${curriculum.displayName || curriculum.name}"?`))) return;
            try {
                await TrailApi.publishCurriculum(curriculum.defnId);
                this.listGen.update((count) => count + 1);
            } catch (caughtError) {
                await AlertService.alert(S25Util.errorText(caughtError));
            }
        };
    };

    disableFactory = (curriculum: CurriculumListItem) => {
        return async () => {
            const name = curriculum.displayName || curriculum.name;
            if (
                !(await AlertService.confirm(
                    `Disable "${name}"? It will no longer be visible. This is a permanent action.`,
                ))
            ) {
                return;
            }
            try {
                await TrailApi.disableCurriculum(curriculum.defnId);
                this.listGen.update((count) => count + 1);
            } catch (caughtError) {
                await AlertService.alert(S25Util.errorText(caughtError));
            }
        };
    };
}
